import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import RemindersPage from './pages/RemindersPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UsersPage from './pages/UsersPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import LeadsPage from './pages/LeadsPage';
import ClientsPage from './pages/ClientsPage';
import ClientManagementPage from './pages/ClientManagementPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import SigningPage from './pages/SigningPage';
import PublicFolderUploadPage from './pages/PublicFolderUploadPage';
import ReportsPage from './pages/ReportsPage';
import PaymentsManagementPage from './pages/PaymentsManagementPage';
import AgentBalancesPage from './pages/AgentBalancesPage';
import AppointmentConfirmationPage from './pages/AppointmentConfirmationPage';
import CardcomResult from './pages/billing/CardcomResult';
import { useNumberInputWheelGuard } from './hooks/useNumberInputWheelGuard';

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: white;
  display: flex;
`;

const MainContent = styled.main`
  flex: 1;
  transition: margin-right 0.3s ease;
  
  @media (max-width: 1023px) {
    margin-right: 0;
  }
`;

function App() {
  // הגנה גלובלית מפני שינוי ערך בשדות type="number" בגלילת גלגלת העכבר
  useNumberInputWheelGuard();

  return (
    <AuthProvider>
      <Router>
        <Toaster
          position="top-center"
          reverseOrder={false}
          toastOptions={{
            duration: 4000,
            style: {
              direction: 'rtl',
              background: 'var(--surface-color)',
              color: 'var(--text-primary)',
            },
            success: {
              iconTheme: {
                primary: 'var(--success-color)',
                secondary: 'white',
              },
            },
            error: {
              iconTheme: {
                primary: 'var(--error-color)',
                secondary: 'white',
              },
            },
          }}
        />
        <Routes>
          {/* Public routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/forgot-password" 
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/reset-password/:token" 
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } 
          />
          
          {/* Public signing route - no authentication required */}
          <Route
            path="/sign/:documentSetId"
            element={<SigningPage />}
          />

          {/* Public folder document upload route - no authentication required */}
          <Route
            path="/folder-upload/:publicId"
            element={<PublicFolderUploadPage />}
          />

          {/* Public appointment confirmation route - no authentication required */}
          <Route
            path="/appointment-confirmation/:token"
            element={<AppointmentConfirmationPage />}
          />

          {/* Cardcom redirect target - loaded inside iframe after card capture, breaks out to top window */}
          <Route
            path="/billing/cardcom-result"
            element={<CardcomResult />}
          />
          
          {/* Unauthorized route - accessible to authenticated users but shows access denied */}
          <Route 
            path="/unauthorized" 
            element={
              <ProtectedRoute>
                <UnauthorizedPage />
              </ProtectedRoute>
            } 
          />

          {/* Protected routes with navigation */}
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <AppContainer>
                  <Navigation />
                  <MainContent>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      {/* Dashboard — כל הצוות (Manager + Employee); סוכנים חסומים */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute capability="IsStaff">
                            <DashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/reminders" element={<RemindersPage />} />

                      {/* Lead Management — כל הצוות (Manager + Employee) */}
                      <Route
                        path="/leads"
                        element={
                          <ProtectedRoute capability="IsStaff">
                            <LeadsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Client Management — כל הצוות (Manager + Employee) */}
                      <Route
                        path="/clients"
                        element={
                          <ProtectedRoute capability="IsStaff">
                            <ClientsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Individual Client Management — כל הצוות (Manager + Employee) */}
                      <Route
                        path="/clients/:clientId/manage/documents/:folderId"
                        element={
                          <ProtectedRoute capability="IsStaff">
                            <ClientManagementPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/clients/:clientId/manage/:tab?"
                        element={
                          <ProtectedRoute capability="IsStaff">
                            <ClientManagementPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* User Management — Manager בלבד */}
                      <Route
                        path="/users"
                        element={
                          <ProtectedRoute capability="CanManageSettings">
                            <UsersPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* System Settings — Manager בלבד */}
                      <Route
                        path="/system-settings"
                        element={
                          <ProtectedRoute capability="CanManageSettings">
                            <SystemSettingsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Reports — צפייה בדוחות פיננסיים (Manager-אפקטיבי) */}
                      <Route
                        path="/reports"
                        element={
                          <ProtectedRoute capability="CanViewFinancialReports">
                            <ReportsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Payments Management — פיננסי מצרפי: CanViewFinancialReports (הוצאות/הנחות/מעשרות) */}
                      <Route
                        path="/payments-management"
                        element={
                          <ProtectedRoute capability="CanViewFinancialReports">
                            <PaymentsManagementPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Agent Balances (P9b) — יתרות סוכנים/ארגונים: פיננסי מצרפי CanViewFinancialReports */}
                      <Route
                        path="/agent-balances"
                        element={
                          <ProtectedRoute capability="CanViewFinancialReports">
                            <AgentBalancesPage />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </MainContent>
                </AppContainer>
              </ProtectedRoute>
            } 
          />
          </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;